import CopyToClipboard from '@bumble/app/components/CopyToClipboard';
import { useAccount } from '@bumble/app/hooks/use-account';
import { COLORS } from '@bumble/app/theme';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Link,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { createContext, useState } from 'react';
import { NavLink, Link as RouterLink, useNavigate } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import { S3AnalyzerModal } from './pcap-analyzer/S3AnalyzerModal';
import { SwitchAccountDialog } from './SwitchAccountDialog';

export const NavBarContext = createContext({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  hideNavContent: () => () => { },
});

interface NavBarProps {
  hideNavbar?: boolean;
  toggleSideBar: () => void;
  showSideBar?: boolean;
}

const NavBar = ({ hideNavbar = false }: NavBarProps) => {
  const { account, session, authenticate } = useAccount();
  const navigate = useNavigate();
  const [profileEl, setProfileEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [switchAccountOpen, setSwitchAccountOpen] = useState(false);

  const openProfileMenu = Boolean(profileEl);
  const handleProfileClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setProfileEl(event.currentTarget);
  };
  const handleProfileClose = () => {
    setProfileEl(null);
  };
  const handleLogout = () => {
    setProfileEl(null);
    navigate('/logout');
  };

  const [notificationsEl, setNotificationsEl] = useState<null | HTMLElement>(null);
  const openNotificationsMenu = Boolean(notificationsEl);
  const handleNotificationsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setNotificationsEl(event.currentTarget);
  };
  const handleNotificationsClose = () => {
    setNotificationsEl(null);
  };

  const openPcapAnalyzer = () => {
    setOpen(true);
  };

  if (hideNavbar /* || !token */) {
    return null;
  }
  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, background: COLORS.WHITE }}
      >
        <Toolbar>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexDirection="column"
            mt={2}
            sx={{ height: '100%', maxWidth: '248px', width: '100%' }}
          >
            <Link
              component={RouterLink}
              to="/main-dashboard"
              sx={{
                width: 170,
                height: 45,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {account?.logo ? (
                <img
                  src={account?.logo}
                  alt="Account Logo"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                  }}
                />
              ) : (
                <ReactSVG
                  wrapper="span"
                  src="/logos/bumble-logo.svg"
                  beforeInjection={(svg) => {
                    svg.setAttribute('style', `width: 100%; height: 100%;`);
                  }}
                />
              )}
            </Link>
            {account?.logo && (
              <Typography variant="caption" color="primary">
                powered by Bumblebee Networks
              </Typography>
            )}
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap={4}
            mt={2}
            ml="auto"
            pr={2}
          >
            <Button
              variant="outlined"
              color="primary"
              id="upload-btn"
              onClick={openPcapAnalyzer}
              sx={{
                display: { md: 'inline-flex', xs: 'none' },
              }}
            >
              PCAP Analyzer
            </Button>
            {/* <FAQ /> */}
            <Badge badgeContent={0} color="secondary">
              <Box
                component="a"
                href="https://www.bumblebeenet.com/post/intelligent-edge-faq"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Tooltip arrow title="FAQs">
                  <IconButton color="primary" size="small">
                    <HelpOutlineOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Badge>
            {/* <Notifications /> */}
            <Badge badgeContent={0} color="secondary">
              <Tooltip arrow title="Notifications">
                <IconButton
                  aria-label="menu"
                  color="primary"
                  size="small"
                  id="notifications-button"
                  aria-controls={openNotificationsMenu ? 'notifications-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={openNotificationsMenu ? 'true' : undefined}
                  onClick={handleNotificationsClick}
                >
                  <NotificationsNoneOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Badge>
            <Popover
              id="notifications-menu"
              open={openNotificationsMenu}
              anchorEl={notificationsEl}
              onClose={handleNotificationsClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              elevation={2}
            >
              <Box sx={{ width: '100%', maxWidth: '160px' }}>
                <Typography variant="subtitle2" sx={{ p: 5 }}>
                  No notifications.
                </Typography>
              </Box>
            </Popover>
            {/* <Mobile Profile Btn /> */}
            <IconButton
              aria-label="menu"
              color="primary"
              size="small"
              id="icon-profile-button"
              aria-controls={openProfileMenu ? 'profile-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openProfileMenu ? 'true' : undefined}
              onClick={handleProfileClick}
              sx={{ display: { md: 'none', xs: 'inline-flex' } }}
            >
              <AccountCircleOutlinedIcon fontSize="small" />
            </IconButton>
            {/* <Profile Btn /> */}
            <Button
              variant="outlined"
              aria-label="menu"
              color="primary"
              id="profile-button"
              aria-controls={openProfileMenu ? 'profile-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openProfileMenu ? 'true' : undefined}
              onClick={handleProfileClick}
              sx={{
                display: { md: 'inline-flex', xs: 'none' },
              }}
              endIcon={<KeyboardArrowDownIcon fontSize="small" />}
            >
              {account?.username}
            </Button>
            <Menu
              id="profile-menu"
              anchorEl={profileEl}
              open={openProfileMenu}
              onClose={handleProfileClose}
              MenuListProps={{
                'aria-labelledby': 'profile-button',
              }}
              elevation={2}
            >
              <CopyToClipboard
                label={<Typography variant="subtitle2">Account ID: {account?.account_id}</Typography>}
                content={account?.account_id || ''}
              />
              <Divider sx={{ my: 1 }} />
              <MenuItem component={NavLink} to="/account" onClick={handleProfileClose}>
                <ListItemIcon>
                  <SettingsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="subtitle2">Settings</Typography>
              </MenuItem>
              <MenuItem component={NavLink} to="/profile" onClick={handleProfileClose}>
                <ListItemIcon>
                  <PersonOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="subtitle2">My Profile</Typography>
              </MenuItem>
              {session?.session_type === 'iam_user' && (
                <MenuItem
                  onClick={() => {
                    handleProfileClose();
                    setSwitchAccountOpen(true);
                  }}
                >
                  <ListItemIcon>
                    <SyncAltIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="subtitle2">Switch Account</Typography>
                </MenuItem>
              )}
              {session?.session_type !== 'iam_user' && (
                <MenuItem component={NavLink} to="/billing" onClick={handleProfileClose}>
                  <ListItemIcon>
                    <PaymentIcon fontSize="small" />
                  </ListItemIcon>
                  <Typography variant="subtitle2">Billing</Typography>
                </MenuItem>
              )}
              <Divider sx={{ my: 1 }} />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <Typography variant="subtitle2">Logout</Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      {open && <S3AnalyzerModal open={open} handleClose={() => setOpen(false)} />}
      <SwitchAccountDialog open={switchAccountOpen} onClose={() => setSwitchAccountOpen(false)} session={session} authenticate={authenticate} />
    </>
  );
};

export default NavBar;
